import { useMemo } from 'react'

import { InfiniteData } from '@tanstack/react-query'
import { TreeItem, TreeItemIndex } from 'react-complex-tree'

import { useCollections, useSpaces } from '@tribeplatform/react-sdk/hooks'
import {
  Collection,
  CollectionListOrderByEnum,
  EntityListFilterByOperator,
  PaginatedSpace,
  Space,
  SpaceType,
} from '@tribeplatform/react-sdk/types'

import { QUERY_SPACES_LIMIT_FOR_SEARCH } from '../constants.js'
import { createSpaceTreeItem } from '../tree-items/createSpaceTreeItem.js'
import { TreeItemData, TreeItems } from '../types.js'

import { useCollectionSpacesTreeItems } from './useCollectionSpacesTreeItems.js'

export function useCollectionsWithSpaceQuery({
  query,
  activeSpaceTreeIndex,
}: {
  query?: string
  activeSpaceTreeIndex?: TreeItemIndex
}) {
  const needsSpaceQuery = query?.length > 0

  const { data: collections, isLoading: isCollectionsLoading } = useCollections(
    {
      variables: {
        orderBy: CollectionListOrderByEnum.CREATED_AT,
      },
    },
  )

  const nameMatchedCollectionIds = useMemo(() => {
    if (!needsSpaceQuery || !collections) {
      return new Set<string>()
    }
    const lowerQuery = query.toLowerCase()
    return new Set(
      collections
        .filter(c => c.name.toLowerCase().includes(lowerQuery))
        .map(c => c.id),
    )
  }, [needsSpaceQuery, collections, query])

  const noCollectionSpaceTreeItems = useCollectionSpacesTreeItems({
    id: 'noCollectionSpaces',
    variables: {
      type: [SpaceType.Group, SpaceType.Broadcast],
      filterBy: [
        {
          key: 'groupId',
          operator: EntityListFilterByOperator.hasValue,
          value: 'false',
        },
      ],
    },
    enabled: !needsSpaceQuery,
    hideLoading: isCollectionsLoading,
    ensureVisible: activeSpaceTreeIndex,
  })

  const { data: spaces, isLoading: isSpacesLoading } = useSpaces({
    variables: {
      query,
      limit: QUERY_SPACES_LIMIT_FOR_SEARCH,
    },
    fields: {
      collection: 'basic',
    },
    useInfiniteQueryOptions: {
      enabled: needsSpaceQuery,
    },
  })

  const searchResult = useMemo(() => {
    if (!needsSpaceQuery) {
      return null
    }
    return getCollectionsFromSpaces(spaces)
  }, [needsSpaceQuery, spaces])

  const searchCollectionSpaces = useMemo(() => {
    if (!searchResult) {
      return null
    }
    return toCollectionSpacesTreeItem(searchResult)
  }, [searchResult])

  const collectionsFromSpaces = useMemo(() => {
    if (!searchResult) {
      return []
    }
    return Object.values(searchResult).map(c => c.collection)
  }, [searchResult])

  // Merge space-search-derived collections with name-matched collections
  const mergedCollections = useMemo(() => {
    if (!needsSpaceQuery) {
      return collections
    }
    const byId = new Map(collectionsFromSpaces.map(c => [c.id, c]))
    for (const c of collections ?? []) {
      if (nameMatchedCollectionIds.has(c.id) && !byId.has(c.id)) {
        byId.set(c.id, c)
      }
    }
    return Array.from(byId.values())
  }, [
    needsSpaceQuery,
    collectionsFromSpaces,
    collections,
    nameMatchedCollectionIds,
  ])

  const noCollectionFromSpaces = useMemo(() => {
    if (!needsSpaceQuery) {
      return noCollectionSpaceTreeItems
    }

    return (
      spaces?.pages
        .flatMap(page => page.nodes)
        .filter(space => !space.collection)
        .reduce((acc, space) => {
          return { ...acc, ...createSpaceTreeItem(space) }
        }, {} as TreeItems) ?? {}
    )
  }, [needsSpaceQuery, spaces, noCollectionSpaceTreeItems])

  return {
    collections: mergedCollections,
    isLoading: needsSpaceQuery ? isSpacesLoading : isCollectionsLoading,
    noCollectionSpaceTreeItems: noCollectionFromSpaces,
    nameMatchedCollectionIds,
    searchCollectionSpaces,
  }
}

function getCollectionsFromSpaces(spaces?: InfiniteData<PaginatedSpace>) {
  return (
    spaces?.pages
      .flatMap(page => page.nodes)
      .reduce(
        (acc, space) => {
          if (space.collection) {
            acc[space.collection.id] = {
              collection: space.collection,
              spaces: [...(acc[space.collection.id]?.spaces ?? []), space],
            }
          }

          return acc
        },
        {} as Record<string, { collection: Collection; spaces: Space[] }>,
      ) ?? {}
  )
}

function toCollectionSpacesTreeItem(
  collectionSpaces: Record<string, { collection: Collection; spaces: Space[] }>,
) {
  return Object.values(collectionSpaces).reduce(
    (acc, c) => {
      const treeItems = c.spaces.reduce(
        (acc, s) => {
          return { ...acc, ...createSpaceTreeItem(s) }
        },
        {} as Record<string, TreeItem<TreeItemData>>,
      )

      acc[c.collection.id] = treeItems

      return acc
    },
    {} as Record<string, TreeItems>,
  )
}
